import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';

/**
 * A toast for a move (archive / delete / junk ...) with an [undo] button.
 *
 * Phone-first: the button is a >=44px touch target and stops the tap from reaching the
 * toast's own click-to-dismiss. One undo per toast (a double tap cannot fire it twice); while
 * it runs the toast says "undoing..." and stays open; if it fails (resolves false, or throws)
 * the toast closes and ONE error toast explains (deduped, so a burst of failures does not
 * stack). A success just closes the toast - the row reappearing is the confirmation.
 *
 * Colours are inherited and the border is currentColor, so it reads correctly in both the
 * terminal theme (dark toast) and the default theme.
 */

const DEFAULT_AUTO_CLOSE_MS = 8000;
const UNDO_FAILED_TOAST_ID = 'mail-undo-failed';

let seq = 0;

type ToastUpdate = Parameters<typeof toast.update>[1];

/**
 * toast.update() with NO delay.
 *
 * react-toastify (11) defaults every update to a 100ms delay and applies it from a timer that
 * re-inserts the toast as ACTIVE. A dismiss() that lands inside that window is therefore undone
 * 100ms later: an undo that fails fast (fetch rejecting while offline, a restore refused without
 * any request) called onFinish -> dismiss first, and the still-pending "keep it open" update then
 * resurrected the toast as a stuck, un-closable "undoing..." - autoClose:false and
 * closeOnClick:false had been applied, so only the X could remove it. The options are spread over
 * the library's defaults, so an explicit null delay overrides the 100ms and the update is applied
 * synchronously: nothing is left pending for a dismiss to lose to. (undoToast.test.tsx runs the
 * real ToastContainer so a library upgrade that changes this fails loudly.)
 */
function updateToastNow(id: string, options: NonNullable<ToastUpdate>): void {
  toast.update(id, { ...options, delay: null });
}

export interface MoveUndoToastOptions {
  message: string;
  /** Resolves true when the undo was applied. Anything else (false / a throw) is a failure. */
  onUndo: () => Promise<boolean>;
  /** ms before the toast closes itself. Default 8000. */
  autoClose?: number;
  /** Pass a stable id to replace an earlier toast for the same thing instead of stacking. */
  toastId?: string;
}

const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 12 };
const messageStyle: React.CSSProperties = { flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere', lineHeight: 1.35 };
const buttonStyle: React.CSSProperties = {
  flex: '0 0 auto',
  minHeight: 44,
  minWidth: 44,
  padding: '0 12px',
  background: 'transparent',
  color: 'inherit',
  border: '1px solid currentColor',
  borderRadius: 0,
  font: 'inherit',
  fontWeight: 600,
  cursor: 'pointer',
  touchAction: 'manipulation',
};

function UndoToastBody(props: {
  message: string;
  onUndo: () => Promise<boolean>;
  onStart: () => void;
  onFinish: (ok: boolean) => void;
}): React.ReactElement {
  const { message, onUndo, onStart, onFinish } = props;
  const [undoing, setUndoing] = useState(false);
  // A ref, not just state: two taps in the same frame both see undoing === false.
  const started = useRef(false);

  const handleClick = async (ev: React.MouseEvent<HTMLButtonElement>) => {
    // The toast closes on click; a tap on the button is an action, not a dismissal.
    ev.stopPropagation();
    if (started.current) return;
    started.current = true;
    setUndoing(true);
    onStart();
    let ok = false;
    try {
      ok = (await onUndo()) === true;
    } catch (err) {
      console.warn('[undoToast] undo threw:', err);
    }
    onFinish(ok);
  };

  return (
    <div style={rowStyle}>
      <span style={messageStyle}>{message}</span>
      <button
        type="button"
        style={{ ...buttonStyle, opacity: undoing ? 0.7 : 1, cursor: undoing ? 'default' : 'pointer' }}
        disabled={undoing}
        aria-label={undoing ? 'undoing' : 'undo'}
        onClick={handleClick}
      >
        {undoing ? 'undoing...' : '[undo]'}
      </button>
    </div>
  );
}

export function showMoveUndoToast(opts: MoveUndoToastOptions): void {
  const autoClose = typeof opts.autoClose === 'number' && opts.autoClose > 0 ? opts.autoClose : DEFAULT_AUTO_CLOSE_MS;
  seq += 1;
  const key = `undo-${seq}`;
  const toastId = opts.toastId || key;

  const body = (
    <UndoToastBody
      // A fresh key per call: replacing a toast in place must not inherit the old "already
      // undone" state, or the new toast's button would be dead.
      key={key}
      message={opts.message}
      onUndo={opts.onUndo}
      onStart={() => {
        // Keep it on screen while the request runs: no timer, no tap-to-dismiss. Applied
        // synchronously (see updateToastNow) so a fast failure's dismiss cannot be overtaken.
        updateToastNow(toastId, { autoClose: false, closeOnClick: false, draggable: false });
      }}
      onFinish={(ok) => {
        toast.dismiss(toastId);
        if (!ok) toast.error("couldn't undo that move", { toastId: UNDO_FAILED_TOAST_ID });
      }}
    />
  );

  if (opts.toastId && toast.isActive(toastId)) {
    // Same id already showing: swap the content and restart the timer rather than being ignored.
    // Synchronous for the same reason as onStart: a pending delayed swap could resurrect a toast
    // that was dismissed in the meantime.
    updateToastNow(toastId, { render: body, autoClose, closeOnClick: true, draggable: true });
    return;
  }
  toast(body, { toastId, autoClose });
}
